// eChartMixed.js
import * as echarts from 'echarts/core';

// CHART TYPES you use
import { BarChart, LineChart, ScatterChart } from 'echarts/charts';

// COMPONENTS referenced in your option
import {
    GridComponent,
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    DataZoomComponent,
    MarkLineComponent
} from 'echarts/components';

import { LabelLayout } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';

echarts.use([
    BarChart,
    LineChart,
    ScatterChart,
    GridComponent,
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    DataZoomComponent,
    MarkLineComponent,
    LabelLayout,
    SVGRenderer
]);

export default echarts;

let charts = [];

function getZoomConfig(chartData) {
    let zoomThreshold =
        window.innerWidth < 768
            ? chartData.axisData.mobileZoomThreshold ?? 10
            : chartData.axisData.zoomThreshold ?? 20;

    let zoomStartDistance =
        window.innerWidth < 768
            ? chartData.axisData.mobileZoomStartDistance ?? 30 // mobile breakpoint (example value)
            : window.innerWidth < 1024
            ? chartData.axisData.mobileZoomStartDistance ?? 40 // tablet breakpoint (example value)
            : chartData.axisData.zoomStartDistance ?? 40; // default for larger screens

    return chartData.labels.length > zoomThreshold
        ? [
            {
                type: "slider",
                orient: "horizontal",
                xAxisIndex: [0, 1],
                start: 0,
                end: zoomStartDistance, // Use dynamic zoomStartDistance,
                showDetail: false, //remove labels from datazoom
            },
        ]
        : []; // No zoom if the threshold isn't met
}

function convertTimeToMinutes(timeString) {
    if (typeof timeString !== "string") {
        return timeString; // If it's already a number, return it as is
    }

    let parts = timeString.split(":").map(Number);
    let hours = parts[0] || 0;
    let minutes = parts[1] || 0;
    let seconds = parts[2] || 0;
    let totalMinutes = hours * 60 + minutes + seconds / 60;

    return parseFloat(totalMinutes.toFixed(2));
}

function preprocessChartData(chartData) {
    chartData.datasets.forEach((dataset) => {
        if (dataset.dataType === "time") {
            dataset.data = dataset.data.map(convertTimeToMinutes);
        }
    });
    return chartData;
}

function getGroupRanges(labels) {
    if (!Array.isArray(labels) || labels.length === 0) {
        return [];
    }

    const ranges = [];
    let currentGroup = Array.isArray(labels[0]) ? labels[0][1] : null;
    let startIndex = 0;

    for (let i = 1; i < labels.length; i++) {
        const groupName = Array.isArray(labels[i]) ? labels[i][1] : null;
        if (groupName !== currentGroup) {
            ranges.push({
                label: currentGroup,
                start: startIndex,
                end: i - 1,
            });
            currentGroup = groupName;
            startIndex = i;
        }
    }

    ranges.push({
        label: currentGroup,
        start: startIndex,
        end: labels.length - 1,
    });

    return ranges.filter((range) => range.label);
}

function buildGroupBracketGraphic(chart, chartData) {
    if (!chartData?.axisData?.groupBracketGraphic) {
        return [];
    }

    const grid = chart.getModel().getComponent("grid");
    if (!grid?.coordinateSystem) {
        return [];
    }

    const rect = grid.coordinateSystem.getRect();
    const offset = chartData.axisData.groupBracketGraphicOffset ?? 18;
    const tickSize = chartData.axisData.groupBracketGraphicTickSize ?? 8;
    const padding = chartData.axisData.groupBracketGraphicPadding ?? 0;
    const stroke = chartData.axisData.groupBracketGraphicColor ?? "#D9DEE7";
    const lineWidth = chartData.axisData.groupBracketGraphicWidth ?? 1;
    const labelEnabled = chartData.axisData.groupLabelGraphic ?? false;
    const labelOffset =
        chartData.axisData.groupBracketGraphicLabelOffset ?? 12;
    const labelColor =
        chartData.axisData.groupLabelGraphicColor ??
        chartData.axisData.axisLabelColor ??
        "#000000";
    const labelSize = chartData.axisData.groupLabelGraphicFontSize ?? 16;
    const labelWeight =
        chartData.axisData.groupLabelGraphicFontWeight ?? "normal";
    const labelFamily =
        chartData.axisData.groupLabelGraphicFontFamily ??
        chartData.axisData.axisLabelFontFamily ??
        "Inter, Arial, sans-serif";
    const yBase = rect.y + rect.height + offset;

    const ranges = getGroupRanges(chartData.labels);
    const elements = [];

    ranges.forEach((range, index) => {
        const startCoord = chart.convertToPixel(
            { xAxisIndex: 0, yAxisIndex: 0 },
            [range.start, 0]
        );
        const endCoord = chart.convertToPixel(
            { xAxisIndex: 0, yAxisIndex: 0 },
            [range.end, 0]
        );

        const x1 = startCoord[0] - padding;
        const x2 = endCoord[0] + padding;

        const xCenter = (x1 + x2) / 2;

        elements.push(
            {
                id: `group-bracket-${index}-h`,
                type: "line",
                silent: true,
                shape: { x1, y1: yBase, x2, y2: yBase },
                style: { stroke, lineWidth },
            },
            {
                id: `group-bracket-${index}-l`,
                type: "line",
                silent: true,
                shape: { x1, y1: yBase, x2: x1, y2: yBase - tickSize },
                style: { stroke, lineWidth },
            },
            {
                id: `group-bracket-${index}-r`,
                type: "line",
                silent: true,
                shape: { x1: x2, y1: yBase, x2, y2: yBase - tickSize },
                style: { stroke, lineWidth },
            }
        );

        if (labelEnabled) {
            elements.push({
                id: `group-bracket-${index}-label`,
                type: "text",
                silent: true,
                style: {
                    text: range.label,
                    x: xCenter,
                    y: yBase + labelOffset,
                    fill: labelColor,
                    fontSize: labelSize,
                    fontWeight: labelWeight,
                    fontFamily: labelFamily,
                    align: "center",
                    verticalAlign: "bottom",
                },
            });
        }
    });

    return elements;
}

function buildEmptyXAxisTitleGraphic(chart, chartData) {
    if (!chartData?.axisData?.emptyXAxisTitleGraphic) {
        return [];
    }

    const grid = chart.getModel().getComponent("grid");
    if (!grid?.coordinateSystem) {
        return [];
    }

    const rect = grid.coordinateSystem.getRect();

    return [
        {
            id: "empty-x-axis-title",
            type: "text",
            silent: true,
            style: {
                text: chartData.axisData.xTitle ?? "",
                x: rect.x + rect.width / 2,
                y:
                    rect.y +
                    rect.height +
                    (chartData.axisData.emptyXAxisTitleGraphicOffset ?? 28),
                fill:
                    chartData.axisData.emptyXAxisTitleGraphicColor ??
                    chartData.axisData.axisLabelColor ??
                    "#376272",
                fontSize:
                    chartData.axisData.emptyXAxisTitleGraphicFontSize ?? 16,
                fontFamily:
                    chartData.axisData.emptyXAxisTitleGraphicFontFamily ??
                    "Inter, Arial, sans-serif",
                align: "center",
                verticalAlign: "top",
            },
        },
    ];
}

function renderChartGraphics(chart, chartData) {
    const elements = [
        ...buildGroupBracketGraphic(chart, chartData),
        ...buildEmptyXAxisTitleGraphic(chart, chartData),
    ];

    chart.setOption({ graphic: { elements } }, { silent: true });
}

function formatMinutesToTime(value) {
    let hours = Math.floor(value / 60);
    let minutes = Math.floor(value % 60);
    let seconds = Math.round((value - Math.floor(value)) * 60);

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
    )}:${String(seconds).padStart(2, "0")}`;
}

function isZeroish(v) {
    const n = Number(v);
    return Number.isFinite(n) && Math.abs(n) < 1e-9; // catches 0 and -0
}

function hasRenderableValue(value) {
    if (Array.isArray(value)) {
        return value.some(hasRenderableValue);
    }

    if (value === null || value === undefined || value === "") {
        return false;
    }

    if (typeof value === "number") {
        return !Number.isNaN(value);
    }

    return true;
}

function hasChartData(chartData) {
    return chartData.datasets.some((dataset) =>
        Array.isArray(dataset.data) && dataset.data.some(hasRenderableValue)
    );
}

function initializeCharts() {
    let chartContainers = document.querySelectorAll(".eChartMixed");

    chartContainers.forEach((chartContainer, index) => {
        let chartDataJson = chartContainer.getAttribute("data-chart");

        // Ensure data-chart attribute exists
        if (!chartDataJson) {
            console.error("Missing data-chart attribute on:", chartContainer);
            return;
        }

        let chartData;
        try {
            chartData = JSON.parse(chartDataJson);
        } catch (error) {
            console.error("Error parsing JSON data:", error);
            return;
        }

        chartData = preprocessChartData(chartData);
        let myChart =
            charts[index] ||
            echarts.init(chartContainer, null, { renderer: "svg" });

        const axisLabelColor = chartData.axisData.axisLabelColor ?? "#000000";
        const isEmptyChart = !hasChartData(chartData);
        const emptyXAxisLabelMargin =
            chartData.axisData.emptyXAxisLabelMargin ?? 18;
        const useEmptyXAxisTitleGraphic =
            isEmptyChart &&
            !chartData.labels.length &&
            Boolean(chartData.axisData.xTitle);

        chartData.axisData.emptyXAxisTitleGraphic = useEmptyXAxisTitleGraphic;

        const leftAxisTitleGap = chartData.axisData.yTitleLeftGap ?? 80;
        const rightAxisTitleGap = chartData.axisData.yTitleRightGap ?? 80;
        const leftGridPadding = chartData.axisData.yTitleLeft
            ? Math.max(
                  chartData.axisData.chartLeftPadding ?? 60,
                  leftAxisTitleGap + 24
              )
            : chartData.axisData.chartLeftPadding ?? 60;
        const rightGridPadding = chartData.axisData.yTitleRight
            ? Math.max(
                  chartData.axisData.chartRightPadding ?? 60,
                  rightAxisTitleGap + 24
              )
            : chartData.axisData.chartRightPadding ?? 60;

        // Initial configuration
        let option = {
            grid: {
                bottom: chartData.axisData.chartBottomPadding ?? 120,
                left: leftGridPadding,
                right: rightGridPadding,
                top: chartData.axisData.chartTopPadding ?? 20,
                containLabel: true,
            },
            nameTextStyle: {
                color: "#376272",
                fontFamily: "Inter, Arial, sans-serif",
                fontSize: 16,
            },
            textStyle: {
                color: "#000000",
                fontFamily: "Inter, Arial, sans-serif",
                fontSize: 16,
            },
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    show: false,
                },
            },
            legend: false,
            xAxis: (() => {
                const hasGrouping = chartData.labels.every(
                    (label) => Array.isArray(label) && label.length === 2
                );

                if (hasGrouping) {
                    const groupRanges = getGroupRanges(chartData.labels);
                    const groupLabelData = Array(chartData.labels.length).fill("");
                    const groupLabelIndexOffset =
                        chartData.axisData.groupLabelIndexOffset ?? 0;
                    groupRanges.forEach((range) => {
                        const centerIndex =
                            (range.start + range.end) / 2 +
                            groupLabelIndexOffset;
                        let labelIndex = Math.min(
                            range.end,
                            Math.max(range.start, Math.round(centerIndex))
                        );
                        if (groupLabelData[labelIndex]) {
                            let placed = false;
                            for (let shift = 1; shift <= range.end - range.start; shift++) {
                                const right = labelIndex + shift;
                                const left = labelIndex - shift;
                                if (right <= range.end && !groupLabelData[right]) {
                                    labelIndex = right;
                                    placed = true;
                                    break;
                                }
                                if (left >= range.start && !groupLabelData[left]) {
                                    labelIndex = left;
                                    placed = true;
                                    break;
                                }
                            }
                            if (!placed) {
                                return;
                            }
                        }
                        groupLabelData[labelIndex] = range.label;
                    });

                    // Grouping logic applied
                    return [
                        {
                            name:
                                chartData.axisData.xTitleAxisIndex === 1 ||
                                useEmptyXAxisTitleGraphic
                                    ? ""
                                    : chartData.axisData.xTitle,
                            nameGap: chartData.axisData.xTitleGap ?? 30,
                            nameLocation:
                                chartData.axisData.xTitleLocation ?? "left",
                            type: "category",
                            data: chartData.labels.map((label) => label[0]),
                            axisLabel: {
                                color: axisLabelColor,
                                formatter: function (value) {
                                    if (Array.isArray(value)) {
                                        return value.join("\n"); // Join elements with a line break
                                    }
                                    return value; // Return value as is if it's a string
                                },
                                align: "center",
                                fontSize: 16,
                                margin:
                                    chartData.axisData.xAxisLabelMargin ??
                                    (isEmptyChart ? emptyXAxisLabelMargin : 10),
                                rotate: chartData.axisData.xRotation ?? 0,
                                interval: 0, // Show all labels
                                width: chartData.axisData.xAxisLabelWidth ?? 85, // Set a max width to force wrapping
                                overflow: "break", // Ensures wrapping if width is set
                            },
                            axisTick: {
                                alignWithLabel: true,
                                show: true,
                            },
                            axisLine: {
                                show: chartData.axisData.xAxisShow ?? true,
                                lineStyle: {
                                    color: chartData.axisData.xAxisLineColor ?? "#C7CDD7",
                                },
                            },
                            splitLine: {
                                show: chartData.axisData.xAxisSplitLineShow ?? false,
                                lineStyle: {
                                    color: chartData.axisData.xAxisSplitLineColor ?? "#E1E6EF",
                                },
                            },
                        },
                        {
                            // Second level of X-Axis for Month Grouping
                            type: "category",
                            name:
                                chartData.axisData.xTitleAxisIndex === 1 &&
                                !useEmptyXAxisTitleGraphic
                                    ? chartData.axisData.xTitle
                                    : "",
                            nameGap: chartData.axisData.xTitleGap ?? 30,
                            nameLocation:
                                chartData.axisData.xTitleLocation ?? "middle",
                            data: groupLabelData,
                            position: "bottom",
                            offset: 30,
                            axisLabel: {
                                color: axisLabelColor,
                                fontWeight: "normal",
                                fontSize: 16,
                                align: "center",
                                show: !chartData.axisData.groupLabelGraphic,
                                interval: 0,
                                width:
                                    chartData.axisData.groupLabelWidth ?? 250, // Set a max width to force wrapping
                                overflow: "break",
                                formatter: function (value) {
                                    return value || "";
                                },
                            },
                            axisLine: {
                                show: false,
                            },
                            axisTick: {
                                show: false,
                            },
                            splitLine: {
                                show: false,
                            },
                        },
                    ];
                } else {
                    // No grouping, single-level x-axis
                    return [
                        {
                            name: useEmptyXAxisTitleGraphic
                                ? ""
                                : chartData.axisData.xTitle,
                            nameGap: chartData.axisData.xTitleGap ?? 30,
                            nameLocation: "center",
                            position:
                                chartData.axisData.xAxisPosition ?? "bottom",
                            type: "category",
                            data: chartData.labels,
                            axisLabel: {
                                color: axisLabelColor,
                                fontSize: 16,
                                rotate: chartData.axisData.xRotation ?? 0,
                                interval: 0,
                                margin:
                                    chartData.axisData.xAxisLabelMargin ??
                                    (isEmptyChart ? emptyXAxisLabelMargin : 10),
                                inside: false,
                                hideOverlap: true
                            },
                            axisLine: {
                                show: chartData.axisData.xAxisShow ?? true,
                                lineStyle: {
                                    color: chartData.axisData.xAxisLineColor ?? "#C7CDD7",
                                },
                            },
                            axisTick: {
                                alignWithLabel: true,
                                show: chartData.axisData.xAxisShow ?? true,
                            },
                            splitLine: {
                                show: chartData.axisData.xAxisSplitLineShow ?? false,
                                lineStyle: {
                                    color: chartData.axisData.xAxisSplitLineColor ?? "#E1E6EF",
                                },
                            },
                        },
                    ];
                }
            })(),
            yAxis: [
                {
                    type: "value",
                    name: chartData.axisData.yTitleLeft,
                    position: "left",
                    nameGap: chartData.axisData.yTitleLeftGap ?? 80,
                    nameLocation: "center",
                    nameRotate: 90,
                    inverse: chartData.axisData.yAxisInverse ?? false,
                    axisLabel: {
                        show: chartData.axisData.yAxisShow ?? true,
                        color: axisLabelColor,
                        fontFamily: "Inter, Arial, sans-serif",
                        fontSize: 16,
                        formatter: function (value) {
                            let dataset = chartData.datasets.find(
                                (ds) => ds.yAxisID === "y-axis-left"
                            );
                            const showUnits =
                                chartData.axisData.yAxisLeftShowUnits ?? true;
                            if (dataset?.dataType === "time") {
                                return formatMinutesToTime(value); // Custom formatting for time
                            }
                            if (dataset?.dataType === "decimal") {
                                return showUnits
                                    ? `${value.toFixed(1)} ${
                                          dataset?.units ?? ""
                                      }`
                                    : `${value.toFixed(1)}`;
                            }
                            return showUnits
                                ? `${value} ${dataset?.units ?? ""}`
                                : `${value}`;
                        },
                    },
                    axisLine: {
                        show: chartData.axisData.yAxisLineShow ?? false,
                        lineStyle: {
                            color: chartData.axisData.yAxisLineColor ?? "#C7CDD7",
                        },
                    },
                    axisTick: {
                        show: chartData.axisData.yAxisShow ?? true,
                    },
                    min: (() => {
                        let dataset = chartData.datasets.find(
                            (ds) => ds.yAxisID === "y-axis-left"
                        );
                        if (dataset?.dataType === "time") {
                            return convertTimeToMinutes(
                                dataset?.yAxisLeftMin ?? "00:00:00"
                            );
                        }
                        return dataset?.yAxisLeftMin ?? "";
                    })(),
                    max: (() => {
                        let dataset = chartData.datasets.find(
                            (ds) => ds.yAxisID === "y-axis-left"
                        );
                        if (dataset?.dataType === "time") {
                            return convertTimeToMinutes(
                                dataset?.yAxisLeftMax ?? "05:00:00"
                            );
                        }
                        return dataset?.yAxisLeftMax ?? "";
                    })(),
                    splitLine: {
                        show: chartData.axisData.yAxisSplitLineShow ?? true,
                        lineStyle: {
                            color: chartData.axisData.yAxisSplitLineColor ?? "#E1E6EF",
                        },
                    },
                },
                {
                    type: "value",
                    name: chartData.axisData.yTitleRight,
                    position: "right",
                    nameGap: chartData.axisData.yTitleRightGap ?? 80,
                    nameLocation: "center",
                    nameRotate: 90,
                    axisLabel: {
                        color: axisLabelColor,
                        fontFamily: "Inter, Arial, sans-serif",
                        fontSize: 16,
                        formatter: function (value) {
                            let dataset = chartData.datasets.find(
                                (ds) => ds.yAxisID === "y-axis-right"
                            );
                            const showUnits =
                                chartData.axisData.yAxisRightShowUnits ?? true;
                            if (dataset?.dataType === "time") {
                                return formatMinutesToTime(value);
                            }
                            if (dataset?.dataType === "decimal") {
                                return showUnits
                                    ? `${value.toFixed(1)} ${
                                          dataset?.units ?? ""
                                      }`
                                    : `${value.toFixed(1)}`;
                            }
                            if (!showUnits) {
                                return `${value}`;
                            }
                            return `${value} ${
                                chartData.axisData.yAxisRightUnits ??
                                dataset?.units ??
                                ""
                            }`;
                        },
                    },
                    axisLine: {
                        show: chartData.axisData.yAxisRightLineShow ?? false,
                        lineStyle: {
                            color: chartData.axisData.yAxisRightLineColor ?? "#C7CDD7",
                        },
                    },
                    min: (() => {
                        let dataset = chartData.datasets.find(
                            (ds) => ds.yAxisID === "y-axis-right"
                        );
                        if (dataset?.dataType === "time") {
                            return convertTimeToMinutes(
                                dataset?.yAxisMin ?? "00:00:00"
                            );
                        }
                        return dataset?.yAxisMin ?? "";
                    })(),
                    max: (() => {
                        let dataset = chartData.datasets.find(
                            (ds) => ds.yAxisID === "y-axis-right"
                        );
                        if (dataset?.dataType === "time") {
                            return convertTimeToMinutes(
                                dataset?.yAxisMax ?? "00:00:00"
                            );
                        }
                        return dataset?.yAxisMax ?? "";
                    })(),
                    show: chartData.datasets.some(
                        (ds) => ds.yAxisID === "y-axis-right"
                    ),
                    splitLine: {
                        show: chartData.axisData.yAxisRightSplitLineShow ?? false,
                        lineStyle: {
                            color: chartData.axisData.yAxisRightSplitLineColor ?? "#E1E6EF",
                        },
                    },
                },
            ],
            series: chartData.datasets.map((ds) => {
                let baseConfig = {
                    name: ds.label,
                    type: ds.type,
                    data: ds.data,
                    stack: ds.stack,
                    itemStyle: {
                        color: ds.backgroundColor,
                        borderColor: ds.borderColor,
                        borderRadius: ds.borderRadius ?? [4, 4, 0, 0],
                        borderWidth: ds.borderWidth,
                    },
                    interval: ds.interval ?? "",
                    label: {
                        show: ds.labelShow ?? true,
                        color: ds.borderColor,
                        borderColor: ds.borderColor,
                        formatter: function (params) {
                            const v = params.value;

                            if (isZeroish(v) || v == null) return '';

                            if (ds.dataType === 'time') {
                                return formatMinutesToTime(v);
                            }
                            if (ds.dataType === 'decimal') {
                                const num = Number(v);
                                if (!Number.isFinite(num)) return '';
                                return `${num.toFixed(1)} ${ds?.units ?? ''}`;
                            }
                            if (ds.units === 'kg') {
                                const num = Number(v);

                                if(!Number.isFinite(num)) return '';
                                
                                return `${Number(num.toFixed(3))} ${ds.units}`;
                            }

                            return `${v} ${ds.units ?? ''}`;
                        },
                    },
                    yAxisIndex: ds.yAxisID === "y-axis-right" ? 1 : 0,
                };

                if (ds.type === "line") {
                    Object.assign(baseConfig, {
                        smooth: false,
                        lineStyle: {
                            color: ds.borderColor,
                        },
                        symbol: "circle",
                        symbolSize: ds.pointRadius,
                        itemStyle: {
                            color: ds.borderColor,
                            borderColor: ds.borderColor,
                            borderWidth: 2, // Optional: Adjust border thickness
                        },
                        label: {
                            ...baseConfig.label, // Keep global label settings
                            backgroundColor: ds.backgroundColor,
                            padding: 8,
                            borderRadius: 8,
                            position: ds.labelPosition ?? "top",
                        },
                    });
                }

                if (ds.type === "bar") {
                    Object.assign(baseConfig, {
                        barWidth: ds.barWidth ?? "80%",
                        barGap: ds.barGap ?? "5%",
                        label: {
                            ...baseConfig.label,
                            position: ds.labelPosition ?? "top",
                        },
                    });

                    if (ds.stackOrder === "bottom") {
                        Object.assign(baseConfig, {
                            itemStyle: {
                                color: ds.backgroundColor,
                                borderColor: ds.borderColor,
                                borderWidth: ds.borderWidth,
                                borderRadius: 0,
                            },
                        });
                    }
                }

                if (ds.type === "scatter") {
                    Object.assign(baseConfig, {
                        label: {
                            ...baseConfig.label, // Keep global label settings
                            offset: [0, -10],
                        },
                    });
                }

                if (ds.dataType === "customLine") {
                    Object.assign(baseConfig, {
                        lineStyle: {
                            color: ds.borderColor,
                            width: 0,
                        },
                        symbol: "line",
                        symbolSize: ds.barWidth ?? 40,
                        symbolOffset: [0, -22],
                    });
                }

                if (ds.separateGroups === true) {
                    Object.assign(baseConfig, {
                        markLine: {
                            silent: true,
                            symbol: "none",
                            data: chartData.labels
                                .map((label, index, array) => {
                                    if (
                                        index > 0 &&
                                        label[1] !== array[index - 1][1]
                                    ) {
                                        return {
                                            xAxis: index,
                                        };
                                    }
                                    return null;
                                })
                                .filter((item) => item !== null),
                            lineStyle: {
                                color: "#376272",
                                type: "dashed",
                                width: 1,
                            },
                            label: {
                                show: false,
                            },
                        },
                        label: {
                            ...baseConfig.label,
                            position: ds.labelPosition ?? "inside",
                        },
                    });
                }

                const customIcons = {
                    customIconHydration: {
                        image: "image:///images/chart-icons/opacity.svg",
                        // to use svg path instead, swap to 'path://M16 22.33a5.18 5.18 0 0 1-3.78-1.53 5.04 5.04 0 0 1-1.55-3.73 5.07 5.07 0 0 1 1.56-3.7L16 9.67l3.77 3.7a5.19 5.19 0 0 1 0 7.43A5.18 5.18 0 0 1 16 22.33Zm-4-5.26h8a3.79 3.79 0 0 0-1.17-2.74L16 11.53l-2.83 2.8A3.63 3.63 0 0 0 12 17.07Z'
                    },
                    customIconSoreness: {
                        image: "image:///images/chart-icons/accessibility.svg",
                    },
                    customIconNutrition: {
                        image: "image:///images/chart-icons/fork_spoon.svg",
                    },
                    customIconStress: {
                        image: "image:///images/chart-icons/psychology_alt.svg",
                    },
                };

                if (ds.dataType in customIcons) {
                    Object.assign(baseConfig, {
                        symbol: customIcons[ds.dataType].image,
                        symbolSize: ds.barWidth ?? 40,
                        symbolOffset: [0, 0],
                        itemStyle: {
                            color: ds.borderColor,
                        },
                        label: {
                            show: false,
                        },
                    });
                }

                return baseConfig;
            }),
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    show: false,
                },
                confine: true,
                appendToBody: false,
            },
            dataZoom: getZoomConfig(chartData), // Call getZoomConfig with chartData for dynamic zoom config
        };

        try {
            myChart.setOption(option);
            renderChartGraphics(myChart, chartData);
        } catch (error) {
            console.error("Error setting chart option:", error);
        }

        if (!myChart.__hasGroupBracketListener) {
            myChart.on("datazoom", () => {
                renderChartGraphics(myChart, chartData);
            });
            myChart.__hasGroupBracketListener = true;
        }

        // Update charts array to keep track of initialized charts
        charts[index] = myChart;
    });
}

// Ensure script runs after the DOM has loaded
document.addEventListener("DOMContentLoaded", initializeCharts);

// Resize existing charts instead of reinitializing
window.addEventListener("resize", () => {
    charts.forEach((chart) => {
        // Refresh zoom config when the window is resized
        let chartData = JSON.parse(chart.getDom().dataset.chart); // Assuming `data-chart` is available
        chart.setOption({
            dataZoom: getZoomConfig(chartData),
        });

        chart.resize(); // Resize chart to fit the container
        chartData.axisData.emptyXAxisTitleGraphic =
            !hasChartData(chartData) &&
            !chartData.labels.length &&
            Boolean(chartData.axisData.xTitle);
        renderChartGraphics(chart, chartData);
    });
});
